import { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, Pencil, Save, Trash2 } from "lucide-react";
import {
  useMappingProfile,
  useUpdateMappingColumns,
  useUpdateMappingProfile,
} from "@/hooks/useMappingProfiles";
import { useToast } from "@/hooks/use-toast";
import type {
  ColumnMappingInput,
  TargetSheet,
  TargetColumn,
} from "@/types/mappingProfile";
import {
  TransformConfigEditor,
  transformNeedsConfig,
} from "@/components/mapping/TransformConfigEditor";
import { SearchableSelect } from "@/components/mapping/SearchableSelect";
import {
  TRANSFORM_OPTION_GROUPS,
  transformHint,
} from "@/components/mapping/transformOptions";
import { Input } from "@/components/ui/input";
import { api } from "@/utils/axiosInterceptor";

const NONE_VALUE = "__none__";

interface TargetTemplateInfo {
  template: {
    templateType: string;
    sheets: TargetSheet[];
    groupingKeyColumn: string;
  };
  columnsFlat: { sheet: string; column: TargetColumn }[];
}

const EditMappingProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const {
    profile,
    columns: existingColumns,
    isLoading,
  } = useMappingProfile(id ?? "");
  const { updateColumns, isUpdating: isUpdatingColumns } =
    useUpdateMappingColumns();
  const { updateProfile, isUpdating: isUpdatingProfile } =
    useUpdateMappingProfile();
  const isUpdating = isUpdatingColumns || isUpdatingProfile;

  const [targetTemplate, setTargetTemplate] =
    useState<TargetTemplateInfo | null>(null);
  const [columns, setColumns] = useState<ColumnMappingInput[]>([]);
  const [groupingKeySourceSheet, setGroupingKeySourceSheet] = useState("");
  const [groupingKeySourceColumn, setGroupingKeySourceColumn] = useState("");
  const [sourceSheets, setSourceSheets] = useState<string[]>([]);
  const [sourceColumnsBySheet, setSourceColumnsBySheet] = useState<
    Record<string, string[]>
  >({});
  const [initialized, setInitialized] = useState(false);
  const [renamingSheet, setRenamingSheet] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  // Load target template info and initialize mappings from existing columns
  useEffect(() => {
    if (!profile || initialized) return;

    const loadTemplate = async () => {
      try {
        const templateRes = await api.get<TargetTemplateInfo>(
          `/mapping-profiles/templates/${profile.targetTemplateType}`,
        );
        setTargetTemplate(templateRes.data);

        const groupingCol = templateRes.data.template.groupingKeyColumn;

        // Build the full set of selectable source sheets/columns
        const sheetSet = new Set<string>();
        const colsBySheet: Record<string, Set<string>> = {};
        const addCol = (sheet: string, column: string) => {
          if (!sheet) return;
          sheetSet.add(sheet);
          if (!colsBySheet[sheet]) colsBySheet[sheet] = new Set();
          if (column) colsBySheet[sheet].add(column);
        };

        for (const s of profile.sourceColumns ?? []) {
          for (const c of s.columns) addCol(s.sheet, c);
        }
        for (const col of existingColumns) {
          addCol(col.sourceSheet, col.sourceColumn);
        }

        setSourceSheets(Array.from(sheetSet));
        const colMap: Record<string, string[]> = {};
        for (const [sheet, cols] of Object.entries(colsBySheet)) {
          colMap[sheet] = Array.from(cols);
        }
        setSourceColumnsBySheet(colMap);

        // Set grouping key from profile
        setGroupingKeySourceSheet(profile.groupingKeySourceSheet ?? "");
        setGroupingKeySourceColumn(profile.groupingKeySourceColumn ?? "");

        // Build column mappings from existing data, excluding grouping key
        const mappings: ColumnMappingInput[] = templateRes.data.columnsFlat
          .filter((tc) => tc.column.name !== groupingCol)
          .map((tc) => {
            const existing = existingColumns.find(
              (ec) =>
                ec.targetSheet === tc.sheet &&
                ec.targetColumn === tc.column.name,
            );
            if (existing) {
              return {
                sourceSheet: existing.sourceSheet,
                sourceColumn: existing.sourceColumn,
                targetSheet: tc.sheet,
                targetColumn: tc.column.name,
                transformType: existing.transformType || "none",
                transformConfig: existing.transformConfig ?? undefined,
                isRequired: tc.column.required,
                defaultValue: existing.defaultValue,
              };
            }
            return {
              sourceSheet: "",
              sourceColumn: "",
              targetSheet: tc.sheet,
              targetColumn: tc.column.name,
              transformType: "none",
              isRequired: tc.column.required,
            };
          });

        setColumns(mappings);
        setInitialized(true);
      } catch {
        // toast via hook
      }
    };

    loadTemplate();
  }, [profile, existingColumns, initialized]);

  const updateColumnMapping = (
    targetSheet: string,
    targetColumn: string,
    field: keyof ColumnMappingInput,
    value: string,
  ) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.targetSheet === targetSheet && col.targetColumn === targetColumn
          ? { ...col, [field]: value }
          : col,
      ),
    );
  };

  const updateColumnConfig = (
    targetSheet: string,
    targetColumn: string,
    transformConfig: Record<string, unknown>,
  ) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.targetSheet === targetSheet && col.targetColumn === targetColumn
          ? { ...col, transformConfig }
          : col,
      ),
    );
  };

  const handleSourceSheetChange = (
    targetSheet: string,
    targetColumn: string,
    val: string,
  ) => {
    const sheet = val === NONE_VALUE ? "" : val;
    setColumns((prev) =>
      prev.map((col) =>
        col.targetSheet === targetSheet && col.targetColumn === targetColumn
          ? { ...col, sourceSheet: sheet, sourceColumn: "" }
          : col,
      ),
    );
  };

  const handleSourceColumnChange = (
    targetSheet: string,
    targetColumn: string,
    val: string,
  ) => {
    updateColumnMapping(
      targetSheet,
      targetColumn,
      "sourceColumn",
      val === NONE_VALUE ? "" : val,
    );
  };

  const getSourceColumnsForSheet = (sheetName: string): string[] => {
    return [...new Set(sourceColumnsBySheet[sheetName] ?? [])];
  };

  // Renames a source sheet everywhere
  const applySheetRename = (oldName: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;

    setSourceSheets((prev) => [
      ...new Set(prev.map((s) => (s === oldName ? trimmed : s))),
    ]);

    setSourceColumnsBySheet((prev) => {
      const next = { ...prev };
      if (next[oldName]) {
        next[trimmed] = [
          ...new Set([...(next[trimmed] ?? []), ...next[oldName]]),
        ];
        delete next[oldName];
      }
      return next;
    });

    setColumns((prev) =>
      prev.map((col) =>
        col.sourceSheet === oldName ? { ...col, sourceSheet: trimmed } : col,
      ),
    );

    setGroupingKeySourceSheet((prev) => (prev === oldName ? trimmed : prev));
  };

  // Removes a stale sheet name from the selectable list
  const deleteSheet = (sheetName: string) => {
    const stillUsed =
      groupingKeySourceSheet === sheetName ||
      columns.some((col) => col.sourceSheet === sheetName);

    if (stillUsed) {
      toast({
        title: "Can't delete this sheet",
        description: `"${sheetName}" is still used by the grouping key or a column mapping. Rename it or point those mappings elsewhere first.`,
        variant: "destructive",
      });
      return;
    }

    setSourceSheets((prev) => prev.filter((s) => s !== sheetName));
    setSourceColumnsBySheet((prev) => {
      const next = { ...prev };
      delete next[sheetName];
      return next;
    });
  };

  const handleSave = useCallback(async () => {
    if (!id || !targetTemplate) return;

    try {
      const validColumns = columns.filter(
        (c) => c.sourceSheet && c.sourceColumn,
      );

      const groupingCol = targetTemplate.template.groupingKeyColumn;
      const allColumns = [...validColumns];

      if (groupingCol && groupingKeySourceSheet && groupingKeySourceColumn) {
        const targetSheets = targetTemplate.template.sheets.map((s) => s.name);
        for (const sheetName of targetSheets) {
          allColumns.push({
            sourceSheet: groupingKeySourceSheet,
            sourceColumn: groupingKeySourceColumn,
            targetSheet: sheetName,
            targetColumn: groupingCol,
            transformType: "none",
            isRequired: true,
          });
        }
      }

      // The grouping key's sheet/column, plus the current (renamed/pruned)
      // sheet list — without persisting sourceColumns here, a deleted or
      // renamed-away sheet name would just reappear on next load, since it's
      // re-derived from this same snapshot every time the page opens.
      await updateProfile({
        id,
        data: {
          groupingKeySourceSheet: groupingKeySourceSheet || null,
          groupingKeySourceColumn: groupingKeySourceColumn || null,
          sourceColumns: sourceSheets.map((sheet) => ({
            sheet,
            columns: sourceColumnsBySheet[sheet] ?? [],
          })),
        },
      });

      await updateColumns({
        id,
        data: {
          columns: allColumns,
          changeDescription: "Updated column mappings",
        },
      });

      navigate(`/mapping-profiles/${id}`);
    } catch {
      // Error handled by hook toast
    }
  }, [
    id,
    columns,
    groupingKeySourceSheet,
    groupingKeySourceColumn,
    sourceSheets,
    sourceColumnsBySheet,
    targetTemplate,
    updateColumns,
    updateProfile,
    navigate,
  ]);

  if (isLoading || !initialized) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Profile not found.</div>
      </div>
    );
  }

  const strictMode = profile.defaultStrictMode ?? false;
  const isColumnRequired = (col: ColumnMappingInput) =>
    strictMode && !!col.isRequired;
  const isColumnMapped = (col: ColumnMappingInput) =>
    !!(col.sourceSheet && col.sourceColumn);
  const missingRequiredColumns = columns.filter(
    (c) => isColumnRequired(c) && !isColumnMapped(c),
  );
  const groupingKeyMissing = !(
    groupingKeySourceSheet && groupingKeySourceColumn
  );
  const canSave = missingRequiredColumns.length === 0 && !groupingKeyMissing;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/mapping-profiles/${id}`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Edit Column Mappings</h1>
            <p className="text-muted-foreground">
              {profile.name} — {existingColumns.length} columns currently mapped
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={isUpdating || !canSave}>
          {isUpdating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Validation mode + required-field status */}
      <div className="text-sm p-3 rounded-md border bg-slate-50 dark:bg-slate-900 space-y-2">
        <p>
          Validation mode:{" "}
          <span className="font-semibold">
            {strictMode ? "Strict" : "Permissive"}
          </span>{" "}
          <span className="text-muted-foreground">
            {strictMode
              ? "— fields marked * must be mapped."
              : "— only the grouping key is required; QuickBill fills defaults."}
          </span>
        </p>
        {(missingRequiredColumns.length > 0 || groupingKeyMissing) && (
          <div className="text-red-700 dark:text-red-300">
            <p className="font-semibold">
              {missingRequiredColumns.length + (groupingKeyMissing ? 1 : 0)}{" "}
              required item(s) must be set before saving:
            </p>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              {groupingKeyMissing && (
                <li>Grouping Key (POS Reference Number source) is not set</li>
              )}
              {missingRequiredColumns.slice(0, 8).map((c) => (
                <li key={`${c.targetSheet}-${c.targetColumn}`}>
                  {c.targetSheet} / {c.targetColumn}
                </li>
              ))}
              {missingRequiredColumns.length > 8 && (
                <li>…and {missingRequiredColumns.length - 8} more</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Source Sheet Names */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Source Sheet Names</CardTitle>
          <p className="text-xs text-muted-foreground">
            If your POS/PMS system regenerates the export with a new sheet (tab)
            name each time — same columns, different name — rename it here
            instead of re-mapping every column. This updates every mapping that
            references the old name at once. Delete a sheet once nothing maps to
            it anymore (e.g. the old name left behind after a rename) to keep
            this list tidy.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {sourceSheets.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No source sheets recorded on this profile yet.
            </p>
          )}
          {sourceSheets.map((sheet) =>
            renamingSheet === sheet ? (
              <div key={sheet} className="flex items-center gap-2">
                <Input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  placeholder="New sheet name"
                  className="h-8 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      applySheetRename(sheet, renameValue);
                      setRenamingSheet(null);
                    } else if (e.key === "Escape") {
                      setRenamingSheet(null);
                    }
                  }}
                />
                <Button
                  size="sm"
                  onClick={() => {
                    applySheetRename(sheet, renameValue);
                    setRenamingSheet(null);
                  }}
                >
                  Apply
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setRenamingSheet(null)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div
                key={sheet}
                className="flex items-center justify-between gap-2 text-sm rounded-md border px-3 py-1.5"
              >
                <span className="truncate">{sheet}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setRenamingSheet(sheet);
                      setRenameValue(sheet);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Rename
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => deleteSheet(sheet)}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            ),
          )}
        </CardContent>
      </Card>

      {/* Grouping Key */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            Grouping Key (POS Reference Number source)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Source Sheet</Label>
              <SearchableSelect
                value={groupingKeySourceSheet}
                options={sourceSheets}
                placeholder="Select sheet"
                allowClear={false}
                className="h-10 text-sm"
                onChange={(v) => {
                  setGroupingKeySourceSheet(v);
                  setGroupingKeySourceColumn("");
                }}
              />
            </div>
            <div>
              <Label className="text-xs">Source Column</Label>
              <SearchableSelect
                value={groupingKeySourceColumn}
                options={getSourceColumnsForSheet(groupingKeySourceSheet)}
                placeholder="Select column"
                allowClear={false}
                className="h-10 text-sm"
                onChange={setGroupingKeySourceColumn}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Column Mappings by Target Sheet */}
      {targetTemplate?.template.sheets.map((sheet) => {
        const sheetMappings = columns.filter(
          (c) => c.targetSheet === sheet.name,
        );
        if (sheetMappings.length === 0) return null;

        return (
          <Card key={sheet.name}>
            <CardHeader>
              <CardTitle className="text-sm">
                {sheet.name}
                <Badge variant="secondary" className="ml-2">
                  {
                    sheetMappings.filter((m) => m.sourceSheet && m.sourceColumn)
                      .length
                  }
                  /{sheetMappings.length} mapped
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {sheetMappings.map((mapping) => {
                  const targetCol = sheet.columns.find(
                    (c) => c.name === mapping.targetColumn,
                  );
                  const required = strictMode && !!targetCol?.required;
                  const missingRequired =
                    required && !(mapping.sourceSheet && mapping.sourceColumn);

                  return (
                    <div
                      key={`${mapping.targetSheet}-${mapping.targetColumn}`}
                      className={`grid grid-cols-12 gap-2 items-center text-sm rounded px-1 ${
                        missingRequired
                          ? "bg-red-50 dark:bg-red-950/30 ring-1 ring-red-300 dark:ring-red-800"
                          : ""
                      }`}
                    >
                      {/* Target */}
                      <div className="col-span-3 flex items-center gap-1">
                        {required && (
                          <span className="text-destructive">*</span>
                        )}
                        <span
                          className={`truncate ${missingRequired ? "text-red-600 dark:text-red-400 font-medium" : ""}`}
                        >
                          {mapping.targetColumn}
                        </span>
                      </div>

                      {/* Arrow */}
                      <div className="col-span-1 text-center text-muted-foreground">
                        &larr;
                      </div>

                      {/* Source sheet */}
                      <div className="col-span-3">
                        <SearchableSelect
                          value={mapping.sourceSheet}
                          options={sourceSheets}
                          placeholder="Sheet"
                          onChange={(val) =>
                            handleSourceSheetChange(
                              mapping.targetSheet,
                              mapping.targetColumn,
                              val,
                            )
                          }
                        />
                      </div>

                      {/* Source column */}
                      <div className="col-span-3">
                        <SearchableSelect
                          value={mapping.sourceColumn}
                          options={getSourceColumnsForSheet(
                            mapping.sourceSheet,
                          )}
                          placeholder="Column"
                          onChange={(val) =>
                            handleSourceColumnChange(
                              mapping.targetSheet,
                              mapping.targetColumn,
                              val,
                            )
                          }
                        />
                      </div>

                      {/* Transform */}
                      <div className="col-span-2">
                        <Select
                          value={mapping.transformType || "none"}
                          onValueChange={(val) =>
                            updateColumnMapping(
                              mapping.targetSheet,
                              mapping.targetColumn,
                              "transformType",
                              val,
                            )
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TRANSFORM_OPTION_GROUPS.map((group) => (
                              <SelectGroup key={group.label}>
                                <SelectLabel className="text-xs">
                                  {group.label}
                                </SelectLabel>
                                {group.options.map((opt) => (
                                  <SelectItem value={opt.value} key={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {transformHint(mapping.transformType) && (
                        <p className="col-span-12 pl-8 text-xs text-muted-foreground">
                          {transformHint(mapping.transformType)}
                        </p>
                      )}
                      {mapping.sourceSheet &&
                        mapping.sourceColumn &&
                        transformNeedsConfig(
                          mapping.transformType || "none",
                        ) && (
                          <div className="col-span-12 pl-8 pb-1">
                            <TransformConfigEditor
                              transformType={mapping.transformType || "none"}
                              config={mapping.transformConfig ?? null}
                              sourceColumns={getSourceColumnsForSheet(
                                mapping.sourceSheet,
                              )}
                              onChange={(c) =>
                                updateColumnConfig(
                                  mapping.targetSheet,
                                  mapping.targetColumn,
                                  c,
                                )
                              }
                            />
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Bottom Save Button */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => navigate(`/mapping-profiles/${id}`)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Cancel
        </Button>
        <Button onClick={handleSave} disabled={isUpdating || !canSave}>
          {isUpdating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EditMappingProfilePage;
